import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { fractionalMatchPoints, isFractionalLabel } from "../utils/fractionalPoints";

// Renders the rich "round/tournament" view: per-group standings + per-player
// rating progression. Pulls all data from the public API by tournamentNumber,
// so it can be embedded in both the standalone tournament page and the
// league round detail page.

interface PlayerRatingChange {
  name?: string;
  ratingBefore?: number | null;
  ratingAfter?: number | null;
  ratingChange?: number | null;
  ratingNotCounted?: boolean;
}

interface MatchPayload {
  matchIndex?: number;
  groupName?: string | null;
  team1?: string[];
  team2?: string[];
  team1Ids?: number[];
  team2Ids?: number[];
  player1?: string;
  player2?: string;
  player1Id?: number;
  player2Id?: number;
  winner?: number;
  matchResult?: string | null;
  setScores?: string[];
  fractional?: boolean;
  team1RatingBefore?: number | null;
  team1RatingAfter?: number | null;
  team1RatingChange?: number | null;
  team2RatingBefore?: number | null;
  team2RatingAfter?: number | null;
  team2RatingChange?: number | null;
  player1RatingBefore?: number | null;
  player1RatingAfter?: number | null;
  player1RatingChange?: number | null;
  player2RatingBefore?: number | null;
  player2RatingAfter?: number | null;
  player2RatingChange?: number | null;
  playerRatingChanges?: Record<string, PlayerRatingChange>;
}

function findPlayerRatingChangeByName(
  match: MatchPayload,
  name: string,
): PlayerRatingChange | undefined {
  const map = match.playerRatingChanges;

  if (!map) {
    return undefined;
  }

  for (const value of Object.values(map)) {
    if (value && value.name === name) {
      return value;
    }
  }

  return undefined;
}

interface GroupStandingsRow {
  pair: string;
  players: string[];
  matchesPlayed: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  pointsWon: number;
  pointsLost: number;
  fractionalPoints: number;
}

export interface GroupStandings {
  rows: GroupStandingsRow[];
  fractional: boolean;
}

// A match belongs to a FRACTIONAL classification when the API says so, or —
// for older payloads without the flag — when its group label carries the marker.
export function isFractionalMatch(match: MatchPayload): boolean {
  if (typeof match.fractional === "boolean") {
    return match.fractional;
  }

  return isFractionalLabel(match.groupName);
}

function sideOf(team: string[] | undefined, player: string | undefined): string[] {
  if (Array.isArray(team) && team.length > 0) {
    return team;
  }

  return player ? [player] : [];
}

export function computeGroupStandings(
  matches: MatchPayload[],
): Map<string, GroupStandings> {
  const byGroup = new Map<string, MatchPayload[]>();

  for (const m of matches) {
    const g = (m.groupName ?? "").toString().trim() || "GRUPA";

    if (!byGroup.has(g)) {
      byGroup.set(g, []);
    }
    byGroup.get(g)!.push(m);
  }

  const out = new Map<string, GroupStandings>();

  byGroup.forEach((groupMatches, groupName) => {
    const teams = new Map<string, GroupStandingsRow>();
    const fractional = groupMatches.some(isFractionalMatch);

    const ensure = (key: string, players: string[]): GroupStandingsRow => {
      let row = teams.get(key);

      if (!row) {
        row = {
          pair: key,
          players,
          matchesPlayed: 0,
          wins: 0,
          losses: 0,
          setsWon: 0,
          setsLost: 0,
          pointsWon: 0,
          pointsLost: 0,
          fractionalPoints: 0,
        };
        teams.set(key, row);
      }

      return row;
    };

    for (const m of groupMatches) {
      // A singles match names its two sides player1/player2; a doubles match
      // lists them as team1/team2. Both are a "side" for the standings.
      const team1 = sideOf(m.team1, m.player1);
      const team2 = sideOf(m.team2, m.player2);

      if (team1.length === 0 || team2.length === 0) {
        continue;
      }

      const key1 = team1.join(" / ");
      const key2 = team2.join(" / ");
      const row1 = ensure(key1, team1);
      const row2 = ensure(key2, team2);

      row1.matchesPlayed += 1;
      row2.matchesPlayed += 1;

      let s1 = 0;
      let s2 = 0;
      const setScores = Array.isArray(m.setScores) ? m.setScores : [];

      for (const setStr of setScores) {
        const [a, b] = String(setStr).split(":").map((n) => parseInt(n, 10));

        if (Number.isNaN(a) || Number.isNaN(b)) {
          continue;
        }
        row1.pointsWon += a;
        row1.pointsLost += b;
        row2.pointsWon += b;
        row2.pointsLost += a;

        if (a > b) {
          s1 += 1;
        } else if (b > a) {
          s2 += 1;
        }
      }

      row1.setsWon += s1;
      row1.setsLost += s2;
      row2.setsWon += s2;
      row2.setsLost += s1;

      if (fractional) {
        const matchPoints = fractionalMatchPoints(setScores);
        row1.fractionalPoints += matchPoints.side1;
        row2.fractionalPoints += matchPoints.side2;
      }

      if (m.winner === 1) {
        row1.wins += 1;
        row2.losses += 1;
      } else if (m.winner === 2) {
        row1.losses += 1;
        row2.wins += 1;
      }
    }

    const rows = Array.from(teams.values()).sort((a, b) => {
      // FRACTIONAL classifies by summed per-set points; wins are only a tie-break.
      if (fractional && b.fractionalPoints !== a.fractionalPoints) {
        return b.fractionalPoints - a.fractionalPoints;
      }

      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }
      const setDiffA = a.setsWon - a.setsLost;
      const setDiffB = b.setsWon - b.setsLost;

      if (setDiffB !== setDiffA) {
        return setDiffB - setDiffA;
      }

      return (b.pointsWon - b.pointsLost) - (a.pointsWon - a.pointsLost);
    });

    out.set(groupName, { rows, fractional });
  });

  return out;
}

interface ProgressionRow {
  matchIndex: number;
  opponent: string;
  ratingBefore: number;
  ratingAfter: number;
  ratingChange: number;
  ratingNotCounted: boolean;
  result: "W" | "L" | "D";
  displayResult: string;
  displaySetScores: string[];
}

function generateRatingProgression(
  matches: MatchPayload[],
  entityName: string,
  isTeamPlayer: boolean,
): ProgressionRow[] {
  const entries: ProgressionRow[] = [];

  for (const match of matches) {
    let isEntity1 = false;
    let opponent = "";
    let ratingBefore = 0;
    let ratingAfter = 0;
    let ratingChange = 0;
    let ratingNotCounted = false;

    if (isTeamPlayer) {
      const inTeam1 = Array.isArray(match.team1) && match.team1.includes(entityName);
      const inTeam2 = Array.isArray(match.team2) && match.team2.includes(entityName);

      if (!inTeam1 && !inTeam2) {
        continue;
      }

      isEntity1 = inTeam1;
      const playerTeam = isEntity1 ? match.team1 ?? [] : match.team2 ?? [];
      const opponentTeam = isEntity1 ? match.team2 ?? [] : match.team1 ?? [];
      const partner = playerTeam.find((p) => p !== entityName) ?? "?";
      opponent = `${partner} vs ${opponentTeam.join(" / ")}`;

      const playerRating = findPlayerRatingChangeByName(match, entityName);
      ratingBefore = Math.round(playerRating?.ratingBefore ?? 0);
      ratingAfter = Math.round(playerRating?.ratingAfter ?? 0);
      ratingChange = Math.round(playerRating?.ratingChange ?? 0);
      ratingNotCounted = !!playerRating?.ratingNotCounted;
    } else {
      if (match.player1 !== entityName && match.player2 !== entityName) {
        continue;
      }
      isEntity1 = match.player1 === entityName;
      opponent = isEntity1 ? match.player2 ?? "?" : match.player1 ?? "?";
      ratingBefore = Math.round(
        (isEntity1 ? match.player1RatingBefore : match.player2RatingBefore) ?? 0,
      );
      ratingAfter = Math.round(
        (isEntity1 ? match.player1RatingAfter : match.player2RatingAfter) ?? 0,
      );
      ratingChange = Math.round(
        (isEntity1 ? match.player1RatingChange : match.player2RatingChange) ?? 0,
      );
    }

    const matchResult = match.matchResult ?? "";
    const [sets1, sets2] = matchResult
      .split("-")
      .map((n) => parseInt(n, 10));
    const result: "W" | "L" | "D" = (() => {
      if (!matchResult || Number.isNaN(sets1) || Number.isNaN(sets2)) {
        return "D";
      }

      if (isEntity1) {
        if (sets1 > sets2) return "W";
        if (sets1 < sets2) return "L";
        return "D";
      }

      if (sets2 > sets1) return "W";
      if (sets2 < sets1) return "L";

      return "D";
    })();

    const displayResult = (() => {
      if (!matchResult || Number.isNaN(sets1) || Number.isNaN(sets2)) {
        return matchResult;
      }
      return isEntity1 ? matchResult : `${sets2}-${sets1}`;
    })();

    const displaySetScores = (() => {
      const arr = Array.isArray(match.setScores) ? match.setScores : [];

      if (isEntity1) {
        return arr;
      }

      return arr.map((setScore) => {
        const [a, b] = String(setScore).split(":");

        return `${b}:${a}`;
      });
    })();

    entries.push({
      matchIndex: match.matchIndex ?? 0,
      opponent,
      ratingBefore,
      ratingAfter,
      ratingChange,
      ratingNotCounted,
      result,
      displayResult,
      displaySetScores,
    });
  }

  return entries.sort((a, b) => a.matchIndex - b.matchIndex);
}

function ratingTierClass(rating: number): string {
  if (rating >= 2000) return "rating-excellent";
  if (rating >= 1800) return "rating-very-good";
  if (rating >= 1600) return "rating-good";
  if (rating >= 1400) return "rating-average";
  return "rating-beginner";
}

interface TournamentResultsViewProps {
  tournamentNumber: number;
  /** Heading rendered above the results. Hidden if omitted. */
  title?: string;
  /** Render "Wyniki grup" cards. Default: true. */
  showGroupStandings?: boolean;
  /** Render per-player rating progression. Default: true. */
  showProgression?: boolean;
  /** When provided, fetches per-row league points for this league round and
   * renders an extra "Pkt" column in the group standings. */
  leagueRoundId?: number;
}

interface RoundPointsRow {
  entityId: number;
  name: string;
  playerIds: number[];
  playerRatings?: (number | null)[];
  groupName: string | null;
  position: number;
  points: number;
}

interface RoundPointsResponse {
  singles: RoundPointsRow[];
  pairs: RoundPointsRow[];
}

const TournamentResultsView: React.FC<TournamentResultsViewProps> = ({
  tournamentNumber,
  title,
  showGroupStandings = true,
  showProgression = true,
  leagueRoundId,
}) => {
  const [matches, setMatches] = useState<MatchPayload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roundPoints, setRoundPoints] = useState<RoundPointsResponse | null>(null);
  const [openPairKey, setOpenPairKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/public/tournaments/${tournamentNumber}/matches`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) {
          return;
        }
        // singles + teamSingles only — `teams` is the legacy team-aggregated
        // view (no per-player ratings, no groupName) which would duplicate
        // teamSingles rows and surface a fake "GRUPA" fallback group.
        const teamSingles = data.teamSingles ?? [];
        const singles = data.singles ?? [];
        const teams = teamSingles.length === 0 ? data.teams ?? [] : [];
        const all: MatchPayload[] = [...singles, ...teamSingles, ...teams];
        setMatches(all);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        console.error("TournamentResultsView fetch failed:", err);
        setError("Nie udało się załadować wyników.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tournamentNumber]);

  useEffect(() => {
    if (typeof leagueRoundId !== "number") {
      setRoundPoints(null);
      return;
    }

    let cancelled = false;
    fetch(`/api/public/league-rounds/${leagueRoundId}/round-points`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: RoundPointsResponse | null) => {
        if (cancelled || !data) {
          return;
        }

        setRoundPoints(data);
      })
      .catch(() => {
        // No points payload — column stays hidden.
      });

    return () => {
      cancelled = true;
    };
  }, [leagueRoundId]);

  const pointsByPairKey = useMemo(() => {
    const map = new Map<string, { position: number; points: number }>();
    if (!roundPoints) {
      return map;
    }

    const pairKey = (name: string) =>
      name
        .split(" / ")
        .map((p) => p.trim().toUpperCase())
        .sort()
        .join(" / ");

    // The same kolejka can have multiple sibling rounds (LIGA I, LIGA II…),
    // but the same pair/player won't appear across them — sorting-by-name is
    // a stable lookup key. Pairs win on collision (singles also include the
    // doubles participants, but pair rows are what the table renders).
    for (const row of roundPoints.singles) {
      map.set(pairKey(row.name), { position: row.position, points: row.points });
    }

    for (const row of roundPoints.pairs) {
      map.set(pairKey(row.name), { position: row.position, points: row.points });
    }

    return map;
  }, [roundPoints]);

  function lookupPoints(pair: string): { position: number; points: number } | null {
    if (pointsByPairKey.size === 0) {
      return null;
    }

    const key = pair
      .split(" / ")
      .map((p) => p.trim().toUpperCase())
      .sort()
      .join(" / ");

    return pointsByPairKey.get(key) ?? null;
  }

  const showLeaguePointsColumn = typeof leagueRoundId === "number" && pointsByPairKey.size > 0;

  const groupStandings = useMemo(
    () => computeGroupStandings(matches),
    [matches],
  );
  // A kolejka played without groups still has a table to show — it is just one
  // unnamed group, so the heading is dropped instead of the whole section.
  const hasStandings = Array.from(groupStandings.values()).some((g) => g.rows.length > 0);
  const isSingleUnnamedGroup =
    groupStandings.size === 1 && groupStandings.has("GRUPA");

  const isTeamPlayer = useMemo(
    () =>
      matches.some(
        (m) =>
          Array.isArray(m.team1) &&
          Array.isArray(m.team2) &&
          m.playerRatingChanges &&
          Object.keys(m.playerRatingChanges).length > 0,
      ),
    [matches],
  );

  const uniqueEntities = useMemo(() => {
    const set = new Set<string>();

    for (const m of matches) {
      if (isTeamPlayer) {
        (m.team1 ?? []).forEach((p) => set.add(p));
        (m.team2 ?? []).forEach((p) => set.add(p));
      } else {
        if (m.player1) set.add(m.player1);
        if (m.player2) set.add(m.player2);
      }
    }

    return set;
  }, [matches, isTeamPlayer]);

  const nameToPlayerId = useMemo(() => {
    const map = new Map<string, number>();

    for (const m of matches) {
      if (Array.isArray(m.team1Ids) && Array.isArray(m.team1)) {
        m.team1.forEach((name, i) => {
          const id = m.team1Ids?.[i];

          if (typeof id === "number" && name) {
            map.set(name, id);
          }
        });
      }
      if (Array.isArray(m.team2Ids) && Array.isArray(m.team2)) {
        m.team2.forEach((name, i) => {
          const id = m.team2Ids?.[i];

          if (typeof id === "number" && name) {
            map.set(name, id);
          }
        });
      }
      if (typeof m.player1Id === "number" && m.player1) {
        map.set(m.player1, m.player1Id);
      }
      if (typeof m.player2Id === "number" && m.player2) {
        map.set(m.player2, m.player2Id);
      }
      if (m.playerRatingChanges) {
        for (const [idKey, entry] of Object.entries(m.playerRatingChanges)) {
          const idNum = Number(idKey);

          if (Number.isFinite(idNum) && entry?.name) {
            map.set(entry.name, idNum);
          }
        }
      }
    }

    return map;
  }, [matches]);

  // Rating per player (current). Prefer round-points payload (snapshot of
   // current PlayerRating); fall back to ratingAfter from the player's last
   // match in this tournament so standalone tournament pages still show numbers.
  const ratingByName = useMemo(() => {
    const map = new Map<string, number>();

    if (roundPoints) {
      const idToName = new Map<number, string>();
      nameToPlayerId.forEach((id, name) => {
        idToName.set(id, name);
      });

      const apply = (rows: RoundPointsRow[]) => {
        for (const row of rows) {
          const ratings = row.playerRatings ?? [];
          row.playerIds.forEach((pid, idx) => {
            const r = ratings[idx];
            if (typeof r !== "number") {
              return;
            }
            const playerName = idToName.get(pid);
            if (playerName) {
              map.set(playerName, r);
            }
          });
        }
      };

      apply(roundPoints.singles);
      apply(roundPoints.pairs);
    }

    if (map.size === 0) {
      for (const m of matches) {
        const changes = m.playerRatingChanges;
        if (!changes) {
          continue;
        }

        for (const entry of Object.values(changes)) {
          if (entry?.name && typeof entry.ratingAfter === "number") {
            map.set(entry.name, entry.ratingAfter);
          }
        }
      }
    }

    return map;
  }, [roundPoints, matches, nameToPlayerId]);

  const playerHref = (name: string): string => {
    const id = nameToPlayerId.get(name);
    const tab = isTeamPlayer ? "?tab=debel" : "";

    if (typeof id === "number") {
      return `/player/${id}${tab}`;
    }

    return `/player/${encodeURIComponent(name)}${tab}`;
  };

  if (loading) {
    return <div className="card">Wczytywanie wyników…</div>;
  }

  if (error) {
    return <div className="card">{error}</div>;
  }

  if (matches.length === 0) {
    return <div className="card">Brak rozegranych meczów w tej kolejce.</div>;
  }

  return (
    <div>
      {title && (
        <h2 style={{ margin: "0 0 1rem 0" }}>{title}</h2>
      )}

      {showGroupStandings && hasStandings && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>{isSingleUnnamedGroup ? "Wyniki" : "Wyniki grup"}</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              gap: "1rem",
            }}
          >
            {Array.from(groupStandings.entries()).map(([groupName, { rows, fractional }]) => (
              <div
                key={groupName}
                className="card"
                style={{ padding: "0.75rem", margin: 0 }}
              >
                {!isSingleUnnamedGroup && (
                  <h4 style={{ margin: "0 0 0.5rem 0" }}>{groupName}</h4>
                )}
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{isTeamPlayer ? "Para" : "Zawodnik"}</th>
                        <th className="numeric">M</th>
                        <th className="numeric">W</th>
                        <th className="numeric">P</th>
                        <th className="numeric">Sety</th>
                        <th className="numeric" title="Małe punkty zdobyte w setach (lotki)">Lotki</th>
                        {fractional && (
                          <th
                            className="numeric"
                            title="Klasyfikacja ułamkowa — punkty za sety (3 dominacja / 2 zwykła wygrana / 1 przegrana lub remis / 0)"
                          >
                            Pkt
                          </th>
                        )}
                        {showLeaguePointsColumn && (
                          <th
                            className="numeric"
                            title="Punkty klasyfikacji ligowej za miejsce w grupie w tej kolejce"
                          >
                            Klasyfikacja
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        const lp = showLeaguePointsColumn
                          ? lookupPoints(row.pair)
                          : null;
                        const displayPosition = lp ? lp.position : idx + 1;
                        const rowKey = `${groupName}|${row.pair}`;
                        const isOpen = openPairKey === rowKey;
                        const colSpan =
                          7 + (fractional ? 1 : 0) + (showLeaguePointsColumn ? 1 : 0);

                        return (
                          <React.Fragment key={row.pair}>
                            <tr
                              onClick={() => setOpenPairKey(isOpen ? null : rowKey)}
                              style={{
                                cursor: "pointer",
                                background: isOpen ? "var(--bg-elevated, #eff6ff)" : undefined,
                              }}
                            >
                              <td className="numeric mono">{displayPosition}</td>
                              <td>
                                <span className="entity-name-rating">
                                  {row.players.map((p, pIdx) => {
                                    const r = ratingByName.get(p) ?? null;
                                    return (
                                      <React.Fragment key={`${row.pair}-${p}`}>
                                        <Link
                                          to={playerHref(p)}
                                          className="player-link"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {p}
                                        </Link>
                                        <span
                                          className={`rating-inline-chip${r == null ? " rating-inline-chip--empty" : ""}`}
                                          title={r == null ? "Brak rankingu" : "Aktualny ranking"}
                                        >
                                          {r == null ? "—" : r}
                                        </span>
                                        {pIdx < row.players.length - 1 && (
                                          <span className="entity-name-rating__sep"> / </span>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </span>
                              </td>
                              <td className="numeric">{row.matchesPlayed}</td>
                              <td className="numeric">{row.wins}</td>
                              <td className="numeric">{row.losses}</td>
                              <td className="numeric mono">
                                {row.setsWon}:{row.setsLost}
                              </td>
                              <td className="numeric mono">
                                {row.pointsWon}:{row.pointsLost}
                              </td>
                              {fractional && (
                                <td className="numeric mono" style={{ fontWeight: 700 }}>
                                  {row.fractionalPoints}
                                </td>
                              )}
                              {showLeaguePointsColumn && (
                                <td className="numeric mono" style={{ fontWeight: 700 }}>
                                  {lp ? lp.points : "—"}
                                </td>
                              )}
                            </tr>
                            {isOpen && (
                              <tr>
                                <td colSpan={colSpan} style={{ padding: "0.5rem 0.75rem", background: "#f1f5f9" }}>
                                  <PairMatchBreakdown
                                    players={row.players}
                                    matches={matches}
                                    groupName={groupName}
                                    fractional={fractional}
                                    playerHref={playerHref}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showProgression && uniqueEntities.size > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>
            Zmiany rankingu{" "}
            <span style={{ fontWeight: 400, opacity: 0.7 }}>
              — {isTeamPlayer ? "debel" : "singiel"}
            </span>
          </h3>
          <div style={{ marginBottom: "1rem" }}>
            <input
              type="text"
              placeholder="Szukaj zawodnika…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(400px, 100%), 1fr))",
              gap: "1rem",
            }}
          >
            {Array.from(uniqueEntities)
              .filter((name) =>
                name.toLowerCase().includes(search.toLowerCase()),
              )
              .map((entityName) => {
                const progression = generateRatingProgression(
                  matches,
                  entityName,
                  isTeamPlayer,
                );
                const tournamentChange = progression.reduce(
                  (sum, m) => sum + m.ratingChange,
                  0,
                );
                const lastAfter =
                  progression.length > 0
                    ? progression[progression.length - 1].ratingAfter
                    : 0;

                return (
                  <div
                    key={entityName}
                    className="card"
                    style={{ padding: "0.75rem", margin: 0 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>
                        <Link
                          to={playerHref(entityName)}
                          className="player-link"
                        >
                          {entityName}
                        </Link>
                      </h4>
                      <div style={{ textAlign: "right" }}>
                        <div
                          className={`rating-badge ${ratingTierClass(lastAfter)}`}
                        >
                          {lastAfter || "—"}
                        </div>
                        <div
                          style={{
                            fontSize: "0.9rem",
                            color: tournamentChange >= 0 ? "green" : "red",
                            marginTop: "0.25rem",
                          }}
                        >
                          {tournamentChange >= 0 ? "+" : ""}
                          {tournamentChange}
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: "0.9rem" }}>
                      {progression.map((m, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0.25rem 0",
                            borderBottom:
                              idx < progression.length - 1
                                ? "1px solid #eee"
                                : "none",
                            gap: "0.5rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                padding: "0.2rem 0.4rem",
                                borderRadius: "3px",
                                fontSize: "0.8rem",
                                fontWeight: "bold",
                                color: "white",
                                backgroundColor:
                                  m.result === "W"
                                    ? "#28a745"
                                    : m.result === "L"
                                    ? "#dc3545"
                                    : "#007bff",
                              }}
                            >
                              {m.result}
                            </span>
                            <span
                              style={{
                                fontSize: "0.8rem",
                                color: "#666",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {m.opponent.includes(" vs ")
                                ? m.opponent
                                : `vs ${m.opponent}`}
                            </span>
                            {m.displayResult && (
                              <span
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {m.displayResult}
                              </span>
                            )}
                            {m.displaySetScores.length > 0 && (
                              <span
                                style={{ fontSize: "0.8rem", color: "#666" }}
                              >
                                ({m.displaySetScores.join(" ")})
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span style={{ fontSize: "0.8rem" }}>
                              {m.ratingBefore} →
                            </span>
                            <span
                              style={{ fontSize: "0.8rem", fontWeight: "bold" }}
                            >
                              {m.ratingAfter}
                            </span>
                            <span
                              style={{
                                fontSize: "0.8rem",
                                color: m.ratingChange >= 0 ? "green" : "red",
                                fontWeight: "bold",
                              }}
                            >
                              ({m.ratingChange >= 0 ? "+" : ""}
                              {m.ratingChange})
                            </span>
                            {m.ratingNotCounted && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  color: "#ff9800",
                                  fontWeight: "bold",
                                  padding: "0.1rem 0.3rem",
                                  backgroundColor: "#fff3cd",
                                  borderRadius: "3px",
                                  border: "1px solid #ff9800",
                                }}
                                title="Mecz nie liczony do rankingu"
                              >
                                U
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

interface PairMatchBreakdownProps {
  players: string[];
  matches: MatchPayload[];
  groupName: string;
  fractional: boolean;
  playerHref: (name: string) => string;
}

const PairMatchBreakdown: React.FC<PairMatchBreakdownProps> = ({
  players,
  matches,
  groupName,
  fractional,
  playerHref,
}) => {
  const playerSet = new Set(players);
  const pairKey = [...players].sort().join(" / ");

  const ownMatches = matches.filter((m) => {
    const mGroup = (m.groupName ?? "").toString().trim() || "GRUPA";
    if (mGroup !== groupName) {
      return false;
    }

    const team1 = Array.isArray(m.team1) ? m.team1 : (m.player1 ? [m.player1] : []);
    const team2 = Array.isArray(m.team2) ? m.team2 : (m.player2 ? [m.player2] : []);
    const k1 = [...team1].sort().join(" / ");
    const k2 = [...team2].sort().join(" / ");

    return k1 === pairKey || k2 === pairKey;
  });

  if (ownMatches.length === 0) {
    return <span style={{ color: "#6b7280", fontSize: 13 }}>Brak meczów.</span>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: "#fff" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #d1d5db", color: "#6b7280" }}>
            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 500 }}>#</th>
            <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 500 }}>Przeciwnik</th>
            <th style={{ padding: "6px 8px", textAlign: "center", fontWeight: 500 }}>Sety</th>
            <th style={{ padding: "6px 8px", textAlign: "center", fontWeight: 500 }}>Wynik</th>
            {fractional && (
              <th style={{ padding: "6px 8px", textAlign: "center", fontWeight: 500 }}>Pkt</th>
            )}
          </tr>
        </thead>
        <tbody>
          {ownMatches.map((m, idx) => {
            const team1 = Array.isArray(m.team1) ? m.team1 : (m.player1 ? [m.player1] : []);
            const team2 = Array.isArray(m.team2) ? m.team2 : (m.player2 ? [m.player2] : []);
            const isSide1 = [...team1].sort().join(" / ") === pairKey;
            const opponents = isSide1 ? team2 : team1;
            const setScores = Array.isArray(m.setScores) ? m.setScores : [];
            const won = (isSide1 && m.winner === 1) || (!isSide1 && m.winner === 2);
            const lost = (isSide1 && m.winner === 2) || (!isSide1 && m.winner === 1);
            const resultLabel = won ? "W" : lost ? "L" : "—";
            const resultColor = won ? "#059669" : lost ? "#dc2626" : "#9ca3af";

            const displaySets = setScores.map((s) => {
              const [a, b] = String(s).split(":");
              if (!isSide1) {
                return `${b}:${a}`;
              }

              return `${a}:${b}`;
            });

            const matchPoints = fractional ? fractionalMatchPoints(displaySets).side1 : null;

            return (
              <tr key={`${m.matchIndex ?? idx}-${idx}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "6px 8px", color: "#6b7280" }}>{idx + 1}</td>
                <td style={{ padding: "6px 8px" }}>
                  {opponents.map((o, i) => (
                    <React.Fragment key={`${o}-${i}`}>
                      <Link to={playerHref(o)} className="player-link" style={{ color: playerSet.has(o) ? "#9ca3af" : undefined }}>
                        {o}
                      </Link>
                      {i < opponents.length - 1 && " / "}
                    </React.Fragment>
                  ))}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "monospace" }}>
                  {displaySets.length > 0 ? displaySets.join(", ") : "—"}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "center", color: resultColor, fontWeight: 700 }}>
                  {resultLabel}
                </td>
                {fractional && (
                  <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>
                    +{matchPoints}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TournamentResultsView;
